<template>
  <v-container fluid class="width-100" v-if="getPermission('setting:view')">
    <v-layout>
      <v-flex>
        <h3
          class="font-weight-700 mb-0 font-size-24 custom-headline color-custom-blue"
        >
          Claim Category
        </h3>
      </v-flex>
      <v-flex class="text-right">
        <v-btn
          depressed
          class="custom-grey-border custom-bold-button"
          v-on:click="goBack"
        >
          <v-icon small left>mdi-keyboard-backspace</v-icon>
          Back
        </v-btn>
      </v-flex>
    </v-layout>

    <v-row>
      <v-col md="10">
        <perfect-scrollbar
          :options="{ suppressScrollX: true }"
          class="scroll custom-box-top-inner-shadow"
          style="max-height: 85vh; position: relative"
        >
          <!-- MAIN CARD -->
          <v-card flat class="custom-grey-border remove-border-radius">
            <v-card-title class="headline grey lighten-4">
              <v-row>
                <v-col md="6" class="py-0 my-auto">
                  <h3
                    class="font-weight-700 custom-headline color-custom-blue"
                    style="margin: 3.5px 0px"
                  >
                    claim category & Sub-category
                  </h3>
                </v-col>
                <v-col
                  md="6"
                  class="text-right py-0"
                  v-if="getPermission('setting:update') && false"
                >
                  <v-btn
                    depressed
                    small
                    color="cyan"
                    class="white--text"
                    @click="openAddCategoryDialog"
                  >
                    <v-icon small left>mdi-plus-circle</v-icon>
                    <span class="font-size-16 font-weight-600"
                      >Add Category</span
                    >
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- CATEGORY TABLE -->
            <v-card-text class="p-6 font-size-16">
              <v-simple-table
                class="simple-table"
                fixed-header
                style="max-height: 450px"
              >
                <template v-slot:default>
                  <!-- TABLE HEADER -->
                  <thead>
                    <tr>
                      <th class="simple-table-th" width="50">#</th>
                      <th class="simple-table-th" width="320">Category Name</th>
                      <th class="simple-table-th" width="120">Max Amount</th>
                      <th class="simple-table-th" width="120">Interval</th>
                      <th class="simple-table-th" width="150">
                        Sub-categories
                      </th>
                      <th class="simple-table-th" width="80">Actions</th>
                    </tr>
                  </thead>

                  <!-- TABLE BODY (IF DATA EXISTS) -->
                  <tbody v-if="paginatedCategories?.length > 0">
                    <tr
                      v-for="(category, index) in paginatedCategories"
                      :key="'cat-' + category.id"
                      class="alternate-listing-row"
                      @click="getOverview(category)"
                    >
                      <!-- SL NO -->
                      <td class="simple-table-td" width="50">
                        {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                      </td>

                      <!-- CATEGORY NAME -->
                      <td class="simple-table-td text-capitalize" width="320">
                        <v-chip
                          small
                          :color="category.color"
                          class="mr-2 text-white"
                          label
                          style="width: 6px !important"
                        ></v-chip>
                        {{ category.claim_type }}
                      </td>

                      <!-- MAX AMOUNT -->
                      <td class="simple-table-td" width="120">
                        <v-badge
                          color="red"
                          :content="category.max_amount"
                          class="ml-2"
                        ></v-badge>
                      </td>

                      <!-- INTERVAL -->
                      <td class="simple-table-td" width="120">
                        {{ category.interval }}
                      </td>

                      <!-- SUBCATEGORY COUNT -->
                      <td class="simple-table-td text-center" width="150">
                        <v-chip small outlined color="blue-grey">
                          {{ category.subcategories?.length || 0 }} items
                        </v-chip>
                      </td>

                      <!-- ACTION BUTTONS -->
                      <td class="simple-table-td text-center" width="80">
                        <v-icon
                          color="green"
                          class="mr-2"
                          size="20"
                          @click.stop="openAddSubcategoryDialog(category)"
                        >
                          mdi-plus-circle
                        </v-icon>

                        <v-icon
                          color="cyan"
                          class="mr-2"
                          size="20"
                          @click.stop="editCategory(category)"
                        >
                          mdi-pencil
                        </v-icon>

                        <v-icon
                          v-if="false"
                          color="red"
                          size="20"
                          @click.stop="deleteCategoryDialogOpen(category)"
                        >
                          mdi-delete
                        </v-icon>
                      </td>
                    </tr>
                  </tbody>

                  <!-- EMPTY DATA -->
                  <tbody v-else>
                    <tr>
                      <td colspan="6" class="py-4">
                        <p
                          class="m-0 row-not-found text-center font-weight-500 font-size-16"
                        >
                          <img
                            :src="$assetURL('media/error/empty.png')"
                            class="row-not-found-image"
                          />
                          Uhh... No categories found.
                        </p>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- Pagination -->
              <v-row
                v-if="categories.length > itemsPerPage"
                justify="end"
                class="align-center pagination_btn listing-footer px-2"
              >
                <p class="m-0 listing-footer-count">
                  Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                  {{ Math.min(currentPage * itemsPerPage, categories.length) }}
                  of {{ categories.length }} entries
                </p>
                <v-spacer></v-spacer>
                <v-pagination
                  v-model="currentPage"
                  :length="pageCount"
                  total-visible="7"
                  color="cyan"
                  @input="handlePageChange"
                ></v-pagination>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- ADD/EDIT CATEGORY DIALOG -->
          <Dialog :common-dialog="categoryDialog" :dialog-width="700">
            <template v-slot:title
              >{{ categoryPayload.id ? "Edit" : "Add" }} Claim
              Category</template
            >
            <template v-slot:body>
              <v-form
                ref="categoryForm"
                v-model.trim="categoryFormValid"
                lazy-validation
                @submit.prevent.stop
              >
                <v-container class="py-0">
                  <v-row>
                    <!-- Category Name -->
                    <v-col md="12">
                      <label class="my-auto fw-500 required mb-1"
                        >Claim Category</label
                      >
                      <TextField
                        v-model.trim="categoryPayload.claim_type"
                        :rules="[(v) => !!v || 'Claim category is required']"
                        dense
                        filled
                        readonly
                        solo
                        flat
                        color="cyan"
                        placeholder="Enter claim category name"
                      ></TextField>
                    </v-col>

                    <!-- Max Amount -->
                    <v-col md="6">
                      <label class="my-auto fw-500 required mb-1"
                        >Max Amount</label
                      >
                      <PriceField
                        v-model.trim="categoryPayload.max_amount"
                        :rules="[(v) => !!v || 'Max amount is required']"
                        type="text"
                        maxlength="15"
                        dense
                        filled
                        solo
                        flat
                        color="cyan"
                        placeholder="Enter max amount"
                      ></PriceField>
                    </v-col>

                    <!-- Interval -->
                    <v-col md="6">
                      <label class="my-auto fw-500 required mb-1"
                        >Interval</label
                      >
                      <v-select
                        :menu-props="{ offsetY: true }"
                        v-model.trim="categoryPayload.interval"
                        :rules="[(v) => !!v || 'Interval is required']"
                        :items="intervalOptions"
                        dense
                        filled
                        solo
                        flat
                        color="cyan"
                        text-color="cyan"
                        placeholder="Select interval"
                      ></v-select>
                    </v-col>

                    <!-- Color Picker -->
                    <v-col md="6">
                      <label class="my-auto fw-500 mb-1">Color</label>
                      <v-color-picker
                        v-model.trim="categoryPayload.color"
                        :swatches="swatches"
                        show-swatches
                        hide-canvas
                        hide-inputs
                        swatches-max-height="100"
                        elevation="2"
                      ></v-color-picker>
                    </v-col>

                    <!-- Approval SECTION -->

                    <v-col
                      md="6"
                      v-if="categoryPayload.id != 4 && categoryPayload.id != 3"
                    >
                      <label class="my-auto fw-500 required mb-1"
                        >Approval Roles</label
                      >
                      <v-select
                        :menu-props="{ offsetY: true }"
                        v-model="categoryPayload.approval"
                        :rules="[
                          (v) =>
                            (v && v.length > 0) ||
                            'At least one approval role is required',
                        ]"
                        :items="approvalOptions"
                        multiple
                        chips
                        dense
                        filled
                        solo
                        flat
                        color="cyan"
                        placeholder="Select approval roles"
                        item-text="text"
                        item-value="value"
                      ></v-select>
                    </v-col>

                    <v-col md="6">
                      <label class="font-weight-500 font-size-16"
                        >Is Attachment Mandatory?</label
                      >
                      <v-switch
                        v-model="categoryPayload.attachment_mandatory"
                        :disabled="pageLoading"
                        inset
                        color="cyan"
                        :true-value="1"
                        :false-value="0"
                      />
                    </v-col>

                    <!-- SUBCATEGORIES SECTION -->
                    <v-col md="12">
                      <div class="mb-2">
                        <!-- Header -->
                        <div class="d-flex align-center mb-2">
                          <span
                            class="font-weight-600 font-size-15 color-custom-blue"
                          >
                            Sub-categories
                          </span>
                          <v-spacer></v-spacer>
                          <!-- Add Button -->
                          <v-btn
                            small
                            color="green lighten-1"
                            @click="addSubcategoryInput"
                            class="white--text"
                            depressed
                          >
                            <v-icon left small>mdi-plus</v-icon>
                            <span class="font-weight-600"
                              >Add Sub-category Field</span
                            >
                          </v-btn>
                        </div>

                        <!-- Table -->
                        <v-simple-table
                          class="subcategory-table elevation-1"
                          v-if="subcategoryInputs.length > 0"
                        >
                          <thead>
                            <tr>
                              <th class="text-left" width="40">#</th>
                              <th class="text-left" width="300">
                                Subcategory Name
                              </th>
                              <th class="text-end">Action</th>
                            </tr>
                          </thead>

                          <tbody>
                            <tr
                              v-for="(sub, index) in subcategoryInputs"
                              :key="'sub-' + sub.tempId"
                              :class="
                                index % 2 === 0 ? 'row-light' : 'row-dark'
                              "
                            >
                              <!-- Row Number -->
                              <td>
                                <v-icon small color="cyan darken-2"
                                  >mdi-checkbox-blank-circle</v-icon
                                >
                              </td>

                              <!-- Subcategory Name Field -->
                              <td>
                                <v-text-field
                                  dense
                                  outlined
                                  solo
                                  flat
                                  hide-details
                                  color="cyan"
                                  v-model="sub.name"
                                  class="px-0"
                                  :placeholder="`Subcategory ${index + 1}`"
                                  :maxlength="100"
                                ></v-text-field>
                              </td>

                              <!-- Action -->
                              <td class="text-end">
                                <v-btn
                                  icon
                                  small
                                  color="red"
                                  @click="removeSubcategoryInput(index)"
                                >
                                  <v-icon small>mdi-close</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </v-simple-table>

                        <!-- Empty State -->
                        <div v-else class="text-center py-3 mb-4 empty-box">
                          <v-icon color="grey lighten-1" size="40" class="mb-2">
                            mdi-folder-open-outline
                          </v-icon>
                          <p class="text--secondary mb-0">
                            <em>No sub-categories added yet</em>
                          </p>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
            </template>
            <template v-slot:action>
              <v-btn @click="closeCategoryDialog" depressed class="mr-2"
                >Cancel</v-btn
              >
              <v-btn
                color="cyan white--text"
                @click="saveCategoryWithSubcategories"
                :loading="categoryLoading"
                depressed
              >
                Save Category
              </v-btn>
            </template>
          </Dialog>

          <!-- CATEGORY/SUB-CATEGORY OVERVIEW DIALOG -->
          <Dialog :common-dialog="categoryDialogOverview" :dialog-width="700">
            <template v-slot:title>Overview</template>
            <template v-slot:body>
              <template>
                <v-card elevation="0">
                  <v-tabs
                    v-model="tabbody"
                    background-color="transparent"
                    color="cyan"
                    class="custom-tab-transparent sticky-tab-0"
                  >
                    <v-tab class="px-8"> Category </v-tab>
                    <v-tab class="px-8"> SUBCATGORIES </v-tab>
                  </v-tabs>

                  <v-tabs-items v-model="tabbody">
                    <v-tab-item class="py-5">
                      <v-card flat class="border border-1 pa-0">
                        <v-card-text class="py-0">
                          <v-row
                            class=""
                            style="background: rgb(241, 241, 241)"
                          >
                            <v-col
                              cols="3"
                              class="color-custom-blue text-uppercase font-weight-bold text-body-1"
                            >
                              Claim Type
                            </v-col>
                            <v-col
                              cols="9"
                              class="text-body-2 pa-2 text-capitalize font-weight-bolder"
                            >
                              <span> : {{ categoryPayload.claim_type }} </span>
                            </v-col>
                          </v-row>

                          <v-row class="">
                            <v-col
                              cols="3"
                              class="color-custom-blue text-uppercase font-weight-bold text-body-1"
                            >
                              MAX AMOUNT
                            </v-col>
                            <v-col
                              cols="9"
                              class="text-body-2 pa-2 font-weight-bolder"
                            >
                              <span> : {{ categoryPayload.max_amount }} </span>
                            </v-col>
                          </v-row>

                          <v-row
                            class=""
                            style="background: rgb(241, 241, 241)"
                          >
                            <v-col
                              cols="3"
                              class="color-custom-blue text-uppercase font-weight-bold text-body-1"
                            >
                              INTERVAL
                            </v-col>
                            <v-col
                              cols="9"
                              class="text-body-2 pa-2 text-capitalize font-weight-bolder"
                            >
                              <span> : {{ categoryPayload.interval }} </span>
                            </v-col>
                          </v-row>

                          <v-row class="">
                            <v-col
                              cols="3"
                              class="color-custom-blue text-uppercase font-weight-bold text-body-1"
                            >
                              COLOR
                            </v-col>
                            <v-col
                              cols="9"
                              class="text-body-2 pa-2 d-flex align-center text-center"
                            >
                              <v-avatar
                                size="16"
                                :color="categoryPayload.color"
                                class="mr-2"
                              >
                              </v-avatar>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </v-tab-item>

                    <v-tab-item>
                      <v-card flat v-if="subcategoryInputs.length > 0">
                        <v-card-text>
                          <div
                            v-for="(item, index) in subcategoryInputs"
                            :key="index"
                          >
                            <div
                              class="d-flex mb-1 border border-1"
                              :style="[
                                index % 2 !== 0
                                  ? { backgroundColor: '#fff' }
                                  : { backgroundColor: 'rgb(241, 241, 241)' },
                              ]"
                            >
                              <v-col
                                cols="6"
                                class="text-body-1 d-flex align-center py-1"
                              >
                                <span class="font-weight-bolder">
                                  {{ item.name }}
                                </span>
                              </v-col>
                              <v-col
                                cols="5"
                                class="text-body-1 d-flex align-center justify-end pa-0 font-weight-medium color-custom-blue"
                              >
                                <v-switch
                                  v-model="switch1"
                                  dense
                                  inset
                                  color="green"
                                ></v-switch>
                              </v-col>
                              <v-col cols="1">
                                <v-btn
                                  icon
                                  x-small
                                  color="red "
                                  @click="removeSubcategoryInput(index)"
                                  class="ml-1 pa-1"
                                >
                                  <v-icon small>mdi-close</v-icon>
                                </v-btn>
                              </v-col>
                            </div>
                          </div>
                        </v-card-text>
                      </v-card>
                      <div
                        v-else
                        class="text-center py-4 mb-4"
                        style="
                          border: 1px dashed #ddd;
                          border-radius: 4px;
                          background-color: #f9f9f9;
                        "
                      >
                        <v-icon color="grey lighten-1" size="40" class="mb-2"
                          >mdi-folder-open-outline</v-icon
                        >
                        <p class="text--secondary mb-0">
                          No subcategories added yet
                        </p>
                      </div>
                    </v-tab-item>
                  </v-tabs-items>
                </v-card>
              </template>
            </template>
            <template v-slot:action>
              <v-btn @click="closeOverviewDialog" depressed class="mr-2"
                >Cancel</v-btn
              >
            </template>
          </Dialog>

          <!-- ADD/EDIT SUBCATEGORY DIALOG -->
          <Dialog :common-dialog="subcategoryDialog" :dialog-width="500">
            <template v-slot:title>
              <div class="d-flex align-center">
                <v-icon color="cyan" class="mr-2">{{
                  subcategoryPayload.id ? "mdi-pencil" : "mdi-plus"
                }}</v-icon>
                {{ subcategoryPayload.id ? "Edit" : "Add" }} Sub-category
              </div>
            </template>
            <template v-slot:body>
              <v-form
                ref="subcategoryForm"
                v-model.trim="subcategoryFormValid"
                lazy-validation
                @submit.prevent.stop
              >
                <v-text-field
                  label="Sub-category Name *"
                  v-model.trim="subcategoryPayload.name"
                  :rules="[(v) => !!v || 'Sub-category name is required']"
                  class="px-0"
                  dense
                  filled
                  solo
                  flat
                  color="cyan"
                  placeholder="Enter sub-category name"
                  hint="Enter a descriptive name for the sub-category"
                  persistent-hint
                ></v-text-field>

                <!-- Category Info -->
                <div
                  v-if="
                    !subcategoryPayload.id && subcategoryPayload.category_id
                  "
                  class="mt-3 pa-3"
                  style="
                    background-color: #e8f4f8;
                    border-radius: 4px;
                    border-left: 3px solid #00bcd4;
                  "
                >
                  <div class="d-flex align-center">
                    <v-icon small color="cyan" class="mr-2"
                      >mdi-information</v-icon
                    >
                    <span class="font-weight-500 font-size-14">
                      This sub-category will be added to:
                      <span class="cyan--text text--darken-2">{{
                        getCategoryName(subcategoryPayload.category_id)
                      }}</span>
                    </span>
                  </div>
                </div>
              </v-form>
            </template>
            <template v-slot:action>
              <v-btn @click="closeSubcategoryDialog" depressed class="mr-2"
                >Cancel</v-btn
              >
              <v-btn
                color="cyan white--text"
                @click="saveSubcategory"
                :loading="subcategoryLoading"
                depressed
              >
                {{ subcategoryPayload.id ? "Update" : "Save" }}
              </v-btn>
            </template>
          </Dialog>

          <!-- DELETE CATEGORY DIALOG -->
          <DeleteDialog :delete-dialog="deleteCategoryDialog">
            <template v-slot:title>Delete Category</template>
            <template v-slot:text>
              <div
                style="font-size: 15px; font-weight: 600; margin-bottom: 5px"
              >
                Are you sure you want to delete category "{{
                  deleteCategoryData?.claim_type
                }}"?
              </div>

              <div style="font-size: 13px">
                This will also delete all associated sub-categories.
              </div>
            </template>

            <template v-slot:action>
              <v-btn @click="deleteCategoryDialog = false">Cancel</v-btn>
              <v-btn
                color="red"
                @click="confirmDeleteCategory"
                :loading="deleteCategoryLoading"
                >Delete</v-btn
              >
            </template>
          </DeleteDialog>

          <!-- DELETE SUBCATEGORY DIALOG -->
          <DeleteDialog :delete-dialog="deleteSubcategoryDialog">
            <template v-slot:title>Delete Sub-category</template>
            <template v-slot:text>
              Are you sure you want to delete sub-category "{{
                deleteSubcategoryData?.name
              }}"?
            </template>
            <template v-slot:action>
              <v-btn @click="deleteSubcategoryDialog = false">Cancel</v-btn>
              <v-btn
                color="red"
                @click="confirmDeleteSubcategory"
                :loading="deleteSubcategoryLoading"
                >Delete</v-btn
              >
            </template>
          </DeleteDialog>
        </perfect-scrollbar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { GET, POST, PATCH, DELETE } from "@/core/services/store/request.module";
import Dialog from "@/view/pages/partials/Dialog.vue";
import DeleteDialog from "@/view/pages/partials/Delete.vue";
import ValidationMixin from "@/core/plugins/validation-mixin";
import { ErrorEventBus, SuccessEventBus } from "@/core/lib/message.lib";

export default {
  name: "claim-category-setting",
  mixins: [ValidationMixin],
  components: { Dialog, DeleteDialog },
  data() {
    return {
      categories: [],
      roles: [],
      categoryPayload: {
        id: null,
        claim_type: "",
        max_amount: null,
        interval: null,
        color: "#FF5252",
        approval: [],
        attachment_mandatory: 0,
      },
      subcategoryInputs: [],
      categoryDialog: false,
      categoryFormValid: true,
      categoryLoading: false,

      subcategoryPayload: { id: null, name: "", category_id: null },
      subcategoryDialog: false,
      subcategoryFormValid: true,
      subcategoryLoading: false,

      deleteCategoryDialog: false,
      deleteCategoryData: null,
      deleteCategoryLoading: false,

      deleteSubcategoryDialog: false,
      deleteSubcategoryData: null,
      deleteSubcategoryLoading: false,
      tabbody: 0,
      categoryDialogOverview: false,
      switch1: true,

      intervalOptions: [
        { text: "Monthly", value: "monthly" },
        { text: "Annually", value: "annualy" },
      ],
      swatches: [
        ["#FF0000", "#AA0000", "#550000"],
        ["#FFFF00", "#AAAA00", "#555500"],
        ["#00FF00", "#00AA00", "#005500"],
        ["#00FFFF", "#00AAAA", "#005555"],
        ["#0000FF", "#0000AA", "#000055"],
      ],

      // Pagination
      itemsPerPage: 10,
      currentPage: 1,
    };
  },
  methods: {
    validateNumber($event) {
      this.limitDecimal($event, this.categoryPayload.max_amount);
      this.limitMaxLength($event, this.categoryPayload.max_amount, 12);
    },
    getOverview(category) {
      try {
        this.categoryPayload = {
          id: category.id,
          claim_type: category.claim_type,
          max_amount: category.max_amount,
          interval: category.interval,
          color: category.color,
          // approval: [],
        };
        this.subcategoryInputs = [...category.subcategories];
        console.log(this.categoryPayload);
        console.log(this.subcategoryInputs);
        this.categoryDialogOverview = false;
      } catch (error) {
        console.log(error);
      }
    },
    closeOverviewDialog() {
      this.categoryDialogOverview = false;

      this.categoryPayload = {
        id: null,
        claim_type: "",
        max_amount: null,
        interval: null,
        color: "#FF5252",
      };
      this.subcategoryInputs = [];
    },
    // ***********************
    getCategories() {
      this.$store
        .dispatch(GET, { url: "setting/claim-category" })
        .then(({ data }) => {
          console.log(data, "setting/claim-category");
          this.categories = Array.isArray(data) ? data : [];
          this.currentPage = 1;
        })
        .catch((err) => console.error(err));
    },

    getRoles() {
      return this.$store
        .dispatch(GET, { url: "role" })
        .then(({ data }) => {
          this.roles = Array.isArray(data) ? data : [];
        })
        .catch((err) => console.error(err));
    },

    // ===== CATEGORY DIALOG METHODS =====
    openAddCategoryDialog() {
      this.categoryPayload = {
        id: null,
        claim_type: "",
        max_amount: null,
        interval: null,
        color: "#FF5252",
        approval: [],
      };
      this.subcategoryInputs = [];
      this.categoryDialog = true;
    },

    addSubcategoryInput() {
      this.subcategoryInputs.push({
        name: "",
        id: null,
        tempId: Date.now() + Math.random(),
      });
    },

    removeSubcategoryInput(index) {
      this.subcategoryInputs.splice(index, 1);
    },

    closeCategoryDialog() {
      this.categoryDialog = false;
      this.categoryPayload = {
        id: null,
        claim_type: "",
        max_amount: null,
        interval: null,
        color: "#FF5252",
        approval: [],
      };
      this.subcategoryInputs = [];
      if (this.$refs.categoryForm) this.$refs.categoryForm.reset();
    },

    saveCategoryWithSubcategories() {
      if (!this.$refs.categoryForm.validate()) {
        return;
      }

      this.categoryLoading = true;

      // Prepare payload
      let payload = { ...this.categoryPayload };

      // Handle color conversion
      if (
        payload.color &&
        typeof payload.color === "object" &&
        payload.color.hexa
      ) {
        payload.color = payload.color.hexa;
      }

      // Add subcategories
      payload.subcategories = this.subcategoryInputs
        .filter((s) => s.name && s.name.trim())
        .map((s) => ({
          id: s.id || null,
          name: s.name.trim(),
        }));

      this.$store
        .dispatch(PATCH, { url: "setting/claim-category", data: payload })
        .then(() => {
          this.closeCategoryDialog();
          this.getCategories();
        })
        .catch((err) => {
          console.error(err);
          // ErrorEventBus.$emit(
          //   "update:error",
          //   err?.response?.data?.message || "Error saving category"
          // );
        })
        .finally(() => (this.categoryLoading = false));
    },

    editCategory(category) {
      this.categoryPayload = {
        id: category.id,
        claim_type: category.claim_type,
        max_amount: category.max_amount,
        interval: category.interval,
        color: category.color || "#FF5252",
        approval: category.approval_roles || [],
        attachment_mandatory: Number(category.attachment_mandatory) || 0,
      };
      this.subcategoryInputs = category.subcategories
        ? category.subcategories.map((s) => ({
            id: s.id,
            name: s.name,
            tempId: s.id || Date.now() + Math.random(),
          }))
        : [];
      this.categoryDialog = true;
    },

    // ===== SUBCATEGORY DIALOG METHODS =====
    openAddSubcategoryDialog(category) {
      this.subcategoryPayload = {
        id: null,
        name: "",
        category_id: category.id,
      };
      this.subcategoryDialog = true;
    },

    closeSubcategoryDialog() {
      this.subcategoryDialog = false;
      this.subcategoryPayload = { id: null, name: "", category_id: null };
      if (this.$refs.subcategoryForm) this.$refs.subcategoryForm.reset();
    },

    saveSubcategory() {
      if (!this.$refs.subcategoryForm.validate()) {
        return;
      }

      this.subcategoryLoading = true;
      const method = this.subcategoryPayload.id ? PATCH : POST;
      const url = this.subcategoryPayload.id
        ? `setting/claim-subcategories/${this.subcategoryPayload.id}`
        : "setting/claim-subcategories";

      this.$store
        .dispatch(method, { url, data: this.subcategoryPayload })
        .then(() => {
          this.getCategories();
          this.closeSubcategoryDialog();
          SuccessEventBus.$emit(
            "update:success",
            this.subcategoryPayload.id
              ? "Sub-category updated successfully!"
              : "Sub-category created successfully!"
          );
        })
        .catch((err) => {
          console.error(err);
          ErrorEventBus.$emit(
            "update:error",
            err?.response?.data?.message || "Error saving sub-category"
          );
        })
        .finally(() => (this.subcategoryLoading = false));
    },

    editSubcategory(category, sub) {
      this.subcategoryPayload = {
        id: sub.id,
        name: sub.name,
        category_id: category.id,
      };
      this.subcategoryDialog = true;
    },

    getCategoryName(categoryId) {
      const category = this.categories.find((cat) => cat.id === categoryId);
      return category ? category.claim_type : "";
    },

    // ===== DELETE METHODS =====
    deleteCategoryDialogOpen(category) {
      this.deleteCategoryData = category;
      this.deleteCategoryDialog = true;
    },

    confirmDeleteCategory() {
      this.deleteCategoryLoading = true;
      this.$store
        .dispatch(DELETE, {
          url: `setting/claim-category/${this.deleteCategoryData.id}`,
        })
        .then(() => {
          this.deleteCategoryDialog = false;
          SuccessEventBus.$emit(
            "update:success",
            "Category deleted successfully!"
          );
          this.getCategories();
        })
        .catch((err) => {
          console.error(err);
          ErrorEventBus.$emit(
            "update:error",
            err?.response?.data?.message || "Error deleting category"
          );
        })
        .finally(() => (this.deleteCategoryLoading = false));
    },

    deleteSubcategoryDialogOpen(category, sub) {
      this.deleteSubcategoryData = sub;
      this.deleteSubcategoryDialog = true;
    },

    confirmDeleteSubcategory() {
      this.deleteSubcategoryLoading = true;
      this.$store
        .dispatch(DELETE, {
          url: `setting/claim-subcategories/${this.deleteSubcategoryData.id}`,
        })
        .then(() => {
          this.deleteSubcategoryDialog = false;
          SuccessEventBus.$emit(
            "update:success",
            "Sub-category deleted successfully!"
          );
          this.getCategories();
        })
        .catch((err) => {
          console.error(err);
          ErrorEventBus.$emit(
            "update:error",
            err?.response?.data?.message || "Error deleting sub-category"
          );
        })
        .finally(() => (this.deleteSubcategoryLoading = false));
    },

    handlePageChange(page) {
      this.currentPage = page;
    },
  },
  mounted() {
    this.getRoles().then(() => {
      this.getCategories();
    });
  },
  computed: {
    pageCount() {
      return Math.ceil(this.categories.length / this.itemsPerPage);
    },
    paginatedCategories() {
      const sorted = [...this.categories].sort((a, b) => b.id - a.id);
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return sorted.slice(start, end);
    },
    approvalOptions() {
      let options = this.roles
        .filter(
          (role) =>
            role.permissions &&
            role.permissions.some(
              (permission) => permission.name === "claim:approve"
            )
        )
        .map((role) => ({
          text: role.name,
          value: role.id,
        }));
      console.log(options, "approval options");
      if (
        this.categoryPayload.approval &&
        Array.isArray(this.categoryPayload.approval)
      ) {
        this.categoryPayload.approval.forEach((approvalId) => {
          const role = this.roles.find((r) => r.id == approvalId);
          if (role && !options.find((o) => o.value == role.id)) {
            options.push({
              text: role.name,
              value: role.id,
            });
          }
        });
      }

      return options;
    },
  },
};
</script>

<style scoped>
.subcategory-input >>> .v-input__slot {
  min-height: 38px !important;
}

.subcategory-input >>> .v-text-field__details {
  margin-bottom: 0 !important;
  padding-top: 2px !important;
}

::v-deep .v-dialog__actions {
  padding: 16px 24px !important;
  border-top: 1px solid #e0e0e0;
}

.v-chip--small {
  height: 24px !important;
}

.simple-table {
  border-radius: 4px;
  overflow: hidden;
}

.simple-table >>> th {
  background-color: #f5f5f5 !important;
}

.row-not-found-image {
  max-width: 200px;
  opacity: 0.7;
  margin-bottom: 16px;
}
</style>
